import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ShoppingBag, Tag, ArrowRight } from "lucide-react";
import api, { gbp } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

function BundleCard({ b, index }) {
  const { addItem } = useCart();
  const add = () => {
    addItem({ id: b.id, slug: b.slug, name: b.name, price: b.bundle_price, images: [b.image], isBundle: true });
    toast.success(`${b.name} added to your basket`);
  };
  return (
    <div data-testid={`bundle-card-${b.slug}`} className="group flex flex-col overflow-hidden border border-border bg-card transition-colors hover:border-primary animate-fade-up md:flex-row" style={{ animationDelay: `${index * 80}ms` }}>
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-secondary md:aspect-auto md:w-2/5">
        <img src={b.image} alt={b.name} className="zoom-img h-full w-full object-cover" />
        <span className="absolute left-0 top-4 flex items-center gap-1.5 bg-destructive px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-white"><Tag className="h-3.5 w-3.5" /> {b.badge}</span>
      </div>
      <div className="flex flex-1 flex-col p-6 md:p-8">
        <span className="overline">{b.discipline} · Bundle</span>
        <h3 className="mt-2 font-display text-2xl font-semibold uppercase leading-tight">{b.name}</h3>
        <p className="mt-1 text-sm font-medium text-accent">{b.tagline}</p>
        <p className="mt-3 text-sm text-muted-foreground">{b.description}</p>

        <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {b.items.map((it, i) => (
            <li key={i} className="flex items-center gap-2 text-sm">
              <Check className="h-4 w-4 shrink-0 text-accent" /> <span className="text-foreground/85">{it.name}</span>
            </li>
          ))}
        </ul>

        <div className="mt-6 flex flex-wrap items-end justify-between gap-4 border-t border-border pt-5">
          <div>
            <div className="flex items-baseline gap-2">
              <span className="font-display text-3xl font-bold">{gbp(b.bundle_price)}</span>
              <span className="text-base text-muted-foreground line-through">{gbp(b.original_price)}</span>
            </div>
            <span className="text-xs font-semibold text-accent">You save {gbp(b.saving)}</span>
          </div>
          <button data-testid={`add-bundle-${b.slug}`} onClick={add} className="flex items-center gap-2 bg-accent px-6 py-3.5 text-xs font-bold uppercase tracking-widest text-accent-foreground hover:brightness-110 transition-[filter]">
            <ShoppingBag className="h-4 w-4" /> Add Bundle
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Bundles() {
  const [bundles, setBundles] = useState([]);
  useEffect(() => { api.get("/bundles").then((r) => setBundles(r.data)).catch(() => {}); }, []);

  return (
    <div>
      <section className="relative flex min-h-[42vh] items-center">
        <img src="https://images.unsplash.com/photo-1570655371646-1108a3515435?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920" alt="Anglers with a carp" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/70 to-background/20" />
        <div className="container-furn relative">
          <span className="overline">Save more, catch more</span>
          <h1 className="mt-3 max-w-2xl font-display text-4xl font-bold uppercase leading-tight md:text-6xl">Deals & Bundles</h1>
          <p className="mt-4 max-w-lg text-base text-foreground/80">Ready-made kits hand-picked by our team — everything you need in one click, at a proper saving.</p>
        </div>
      </section>

      <section className="container-furn py-14">
        <div className="grid gap-6">
          {bundles.map((b, i) => <BundleCard key={b.id} b={b} index={i} />)}
        </div>
        <div className="mt-12 flex flex-col items-center gap-3 border border-border bg-card p-8 text-center">
          <h2 className="font-display text-2xl font-bold uppercase">Prefer to build your own?</h2>
          <p className="max-w-md text-sm text-muted-foreground">Browse the full range and pick exactly what you need for your next session.</p>
          <Link to="/shop" className="mt-2 inline-flex items-center gap-2 bg-primary px-8 py-4 text-xs font-bold uppercase tracking-widest text-primary-foreground hover:brightness-125 transition-[filter]">Shop all tackle <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>
    </div>
  );
}
